import type { CompanyFilters } from "../../types";
import {
  normalizeMinMax,
  normalizeNumberArray,
  normalizeStringArray,
} from "../../utils/normalize";
import type { FilterMode } from "@/components/filters/Condition/types";
import { FILTER_MODE } from "@/components/filters/Condition/constants";
import { defaultProductsSlice, type ProductsSlice } from "./slice";

export function toCompanyFilters(
  slice: ProductsSlice
): Partial<CompanyFilters> {
  type ProductFilterInput = NonNullable<
    NonNullable<CompanyFilters["products"]>[number]
  >;
  // Extract the GraphQL InclusionMethod type from ProductFilterInput to stay decoupled
  type GraphQLInclusionMethod = ProductFilterInput["inclusionMethod"];

  const out: ProductFilterInput[] = [];

  slice.sections.forEach((section) => {
    if (section.productIds.length === 0) return;
    const filter: ProductFilterInput = {
      ids: section.productIds,
      // FilterMode values match GraphQL InclusionMethod enum values
      inclusionMethod: section.inclusionMethod as GraphQLInclusionMethod,
    };
    if (section.departmentIds.length > 0) {
      filter.departmentIds = section.departmentIds;
    }
    if (section.roleIds.length > 0) {
      filter.roleIds = section.roleIds;
    }
    if (section.installAgeMonths) {
      filter.installAgeMonths = section.installAgeMonths;
    }
    if (section.intensity) {
      filter.intensity = section.intensity;
    }
    out.push(filter);
  });

  if (slice.excludedProductIds.length > 0) {
    out.push({
      ids: slice.excludedProductIds,
      inclusionMethod: FILTER_MODE.NONE as GraphQLInclusionMethod,
    });
  }

  return out.length > 0 ? { products: out } : {};
}

export function fromCompanyFilters(filters: CompanyFilters): ProductsSlice {
  type ProductFilterInput = NonNullable<
    NonNullable<CompanyFilters["products"]>[number]
  >;

  const products = filters.products ?? [];

  // Type guard for non-null ProductFilter
  const isProductFilter = (p: unknown): p is ProductFilterInput =>
    p != null && typeof p === "object";

  const sections = products
    .filter(isProductFilter)
    .filter((p) => p.inclusionMethod !== FILTER_MODE.NONE)
    .map((p) => ({
      inclusionMethod: (p.inclusionMethod as FilterMode) || FILTER_MODE.ANY,
      productIds: normalizeNumberArray(p.ids),
      departmentIds: normalizeStringArray(p.departmentIds).map(String),
      roleIds: normalizeStringArray(p.roleIds).map(String),
      installAgeMonths: normalizeMinMax(p.installAgeMonths),
      intensity: normalizeMinMax(p.intensity),
    }))
    .filter((s) => s.productIds.length > 0);

  const exclusion = products
    .filter(isProductFilter)
    .find((p) => p.inclusionMethod === FILTER_MODE.NONE);

  return {
    sections: sections.length > 0 ? sections : defaultProductsSlice.sections,
    excludedProductIds: normalizeNumberArray(exclusion?.ids),
  };
}
